import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl,
  FormHelperText, Typography, Box, Divider, IconButton,
  ToggleButton, ToggleButtonGroup, Grid, Stack,
} from '@mui/material';
import {
  RadioGroup, Radio, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AlertMessage from './AlertMessage';

import { humanReadableDate, titleCase, fitnessMembershipStatusColor, fitnessMembershipStatusText } from '../common/AppUtils';
import { NA_LABEL } from '../common/AppConstants';


const PAYMENT_MODE_CHOICES = [
  { value: 'online', label: 'Online' },
  { value: 'cash', label: 'Cash' },
];

const today = new Date().toISOString().split('T')[0];

const initialRenew = {
  plan: '',
  start_date: today,
  payment_mode: 'online',
  payment_date: today,
};

// ToDo: Should we rely on backend for this to ensure consistency
const getSubscriptionEndDate = (startDate, validityMonths) => {
  const start = new Date(startDate);

  // Preserve original day
  const originalDay = start.getDate();

  // Move to first day to avoid JS month overflow issues
  const nextCycle = new Date(start);
  nextCycle.setDate(1);

  // Add validity months
  nextCycle.setMonth(nextCycle.getMonth() + validityMonths);

  // Get max valid day in target month
  const lastDayOfMonth = new Date(
    nextCycle.getFullYear(),
    nextCycle.getMonth() + 1,
    0
  ).getDate();

  // Restore closest possible original day
  nextCycle.setDate(
    Math.min(originalDay, lastDayOfMonth)
  );

  // Inclusive end date
  nextCycle.setDate(nextCycle.getDate() - 1);

  return nextCycle;
};

function formatDateLabel(date) {
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function MemberRenewModal({
  open,
  member,
  plans = [],
  onClose,
  onSave,
}) {
  const [formData, setFormData] = useState(initialRenew);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const current_subscription = member?.current_membership?.current_subscription;
  const chipColor = fitnessMembershipStatusColor(current_subscription?.status, current_subscription?.date_status, current_subscription?.is_current);

  const planEndDate = React.useMemo(() => {
    const plan = plans?.find(p => p.id === formData.plan);
    if (!formData.start_date || !plan) return '';
    return getSubscriptionEndDate(formData.start_date, plan.validity_months);
  }, [formData.plan, formData.start_date, plans]);

  const finalAmount = React.useMemo(() => {
    return (parseFloat(formData.fee) || 0) - (parseFloat(formData.fee_discount) || 0);
  }, [formData.fee, formData.fee_discount]);

  useEffect(() => {
    if (!open) return;

    const defaultPlan = plans?.find((p) => p.id === current_subscription?.plan) || plans?.[0];
    const currentEndDate = current_subscription?.end_date;
    let defaultStartDate = today;
    if (currentEndDate) {
      const date = new Date(currentEndDate);
      date.setDate(date.getDate() + 1);
      defaultStartDate = date.toISOString().split('T')[0]; // YYYY-MM-DD
    }
    
    setFormData({
      ...initialRenew,
      start_date: defaultStartDate,
      plan: defaultPlan?.id ?? '',
      fee: defaultPlan?.fee ?? '',
      fee_discount: defaultPlan?.fee_discount ?? '',
    });
    
    setErrors({});
  }, [open, plans]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handlePlanChange = (_, value) => {
    const plan_dict = plans?.find(p => p.id === value);
    setFormData(prev => ({
      ...prev,
      plan: value,
      ...(plan_dict && {
        fee: plan_dict.fee,
        fee_discount: plan_dict.fee_discount,
      }),
    }));
    setErrors(prev => ({ ...prev, plan: undefined }));
  };

  const handleSave = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await onSave(
        {
          ...formData,
          final_amount: finalAmount,
          fe_calc: {
            end_date: planEndDate?.toISOString().split('T')[0],
          },
        },
        () => {
          setFormData(initialRenew);
          setErrors({});
        },
        (errs) => setErrors(errs),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const field = (label, attr, type = 'text', required = false, placeholder = '') => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={0.25}>
        {label}{required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
      </Typography>
      <TextField
        fullWidth size="small"
        type={type}
        value={formData[attr] ?? ''}
        onChange={e => handleChange(attr, e.target.value)}
        error={Boolean(errors[attr])}
        helperText={errors[attr]?.[0]}
        placeholder={placeholder}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      />
    </Box>
  );

  const number_field = (label, attr, required = false, editable = true, min = 0, max = undefined) => {
    const value = parseFloat(formData[attr]) || 0;
    const sanitize = (val) => {
      let v = Number.isNaN(val) ? min : Math.max(min, val);
      if (max !== undefined) v = Math.min(v, max);
      return v;
    };

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" mb={0.25}>
          {label}{required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
        </Typography>
        <TextField
          fullWidth size="small"
          type="number"
          value={value}
          onChange={e => handleChange(attr, e.target.value)}
          onBlur={e => handleChange(attr, sanitize(parseFloat(e.target.value)))}
          error={Boolean(errors[attr])}
          helperText={errors[attr]?.[0]}
          disabled={!editable}
        />
      </Box>
    );
  };

  const selectField = (label, attr, choices, required = false, onChange = handleChange) => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={0.25}>
        {label}{required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
      </Typography>
      <FormControl fullWidth size="small" error={Boolean(errors[attr])}>
        <Select
          value={formData[attr] ?? ''}
          onChange={e => onChange(attr, e.target.value)}
          displayEmpty
        >
          {!required && (
            <MenuItem value=""><em style={{ color: '#999' }}>Select</em></MenuItem>
          )}
          {choices.map(c => (
            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
          ))}
        </Select>
        {errors[attr] && <FormHelperText>{errors[attr]?.[0]}</FormHelperText>}
      </FormControl>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          // boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Renew Membership
          </Typography>

          {member && (
            <Typography variant="body2" color="text.secondary">
              {member.full_name}
            </Typography>
          )}
        </Box>
        {true && ( // ToDo: !isSubmitting
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {/* Current details */}
          <Stack spacing={0.75}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Due date</Typography>
              <Typography variant="body2">{humanReadableDate(current_subscription?.end_date) ?? NA_LABEL}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: chipColor }} />
                <Typography variant="body2" fontWeight="bold" sx={{ color: chipColor }}>{fitnessMembershipStatusText(current_subscription?.status, current_subscription?.date_status, current_subscription?.is_current)}</Typography>
              </Box>
            </Box>
          </Stack>

          {/* Plan + subcription details */}
          <Divider sx={{ mt: 1 }}>
            <Typography variant="body1" color="text.secondary">Renewal Detail</Typography>
          </Divider>

          {/* Subscription */}
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              {selectField('Plan', 'plan', plans.map(p => ({ value: p.id, label: p.name })), true, handlePlanChange)}
            </Grid>
            <Grid item xs={6}>
              {field('Start date', 'start_date', 'date', true)}
            </Grid>
          </Grid>

          {planEndDate && (
            <AlertMessage
              severity="info"
              message={`Valid till ${formatDateLabel(planEndDate)}`}
            />
          )}


          {/* Fees + payment details */}
          <Divider sx={{ mt: 1 }}>
            <Typography variant="body1" color="text.secondary">Payment Detail</Typography>
          </Divider>

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              {number_field('Subscription Fee', 'fee', false, false )}
            </Grid>
            <Grid item xs={6}>
              {number_field('Subscription discount', 'fee_discount', false, true, 0, parseFloat(formData.fee) || 0 )}
            </Grid>
          </Grid>

          <AlertMessage
            severity="info"
            message={`Total Amount to be paid = ₹ ${finalAmount} /-`}
          />

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              {selectField('Payment mode', 'payment_mode', PAYMENT_MODE_CHOICES, true)}
            </Grid>
            <Grid item xs={6}>
              {field('Payment date', 'payment_date', 'date', true)}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={isLoading}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
